import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getTemplates, upsertTemplate, seedTemplates, mergeSeedTemplates, detectDrift, fixDrift } from '../../services/envTemplateApi';
import { listApps } from '../../services/envInjectorApi';

const TIER_LABELS = { 0: 'Global', 1: 'Per-Service', 2: 'Per-Tenant' };
const TIER_COLORS = {
  0: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  1: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  2: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
};
const DRIFT_COLORS = {
  1: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',       // Missing
  2: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', // Stale
};
const SETTING_TYPE_LABELS = { 0: 'App Setting', 1: 'Connection String' };

const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${className}`}>
    {children}
  </span>
);

const EnvManager = () => {
  const [activeTab, setActiveTab] = useState('templates');

  // ── Templates state ────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null); // full template being edited
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ── Drift state ────────────────────────────────────────────
  const [apps, setApps] = useState([]);
  const [driftApiType, setDriftApiType] = useState('');
  const [driftReport, setDriftReport] = useState(null);
  const [loadingDrift, setLoadingDrift] = useState(false);
  const [driftError, setDriftError] = useState(null);
  const [selectedDriftItems, setSelectedDriftItems] = useState(new Set());
  const [fixLoading, setFixLoading] = useState(false);
  const [fixResults, setFixResults] = useState(null);

  // ── Load templates on mount ─────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const data = await getTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Load apps for drift detection
  useEffect(() => {
    listApps().then(setApps).catch(console.error);
  }, []);

  // ── Seed handler ─────────────────────────────────────────────
  const handleSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const result = await seedTemplates();
      setSeedResult(result);
      await fetchTemplates();
    } catch (err) {
      setSeedResult({ error: err.message });
    } finally {
      setSeeding(false);
    }
  };

  const handleMergeSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const result = await mergeSeedTemplates();
      setSeedResult(result);
      await fetchTemplates();
    } catch (err) {
      setSeedResult({ error: err.message });
    } finally {
      setSeeding(false);
    }
  };

  // ── Template editing ─────────────────────────────────────────
  const startEdit = (template) => {
    setEditingTemplate(JSON.parse(JSON.stringify(template)));
  };

  const addSetting = () => {
    if (!editingTemplate) return;
    setEditingTemplate({
      ...editingTemplate,
      settings: [...editingTemplate.settings, { name: '', value: '', tier: 0, slotSetting: true, settingType: 0, connectionStringType: null }],
    });
  };

  const removeSetting = (index) => {
    if (!editingTemplate) return;
    setEditingTemplate({
      ...editingTemplate,
      settings: editingTemplate.settings.filter((_, i) => i !== index),
    });
  };

  const updateSetting = (index, field, value) => {
    if (!editingTemplate) return;
    const newSettings = [...editingTemplate.settings];
    newSettings[index] = { ...newSettings[index], [field]: value };
    setEditingTemplate({ ...editingTemplate, settings: newSettings });
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    setSaveLoading(true);
    setSaveError(null);
    try {
      await upsertTemplate({
        apiType: editingTemplate.apiType,
        displayName: editingTemplate.displayName,
        settings: editingTemplate.settings,
      });
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Drift detection ──────────────────────────────────────────
  const handleDetectDrift = async () => {
    if (!driftApiType) return;
    setLoadingDrift(true);
    setDriftError(null);
    setDriftReport(null);
    setFixResults(null);
    setSelectedDriftItems(new Set());
    try {
      const report = await detectDrift(driftApiType, []); // empty = auto-discover
      setDriftReport(report);
    } catch (err) {
      setDriftError(err.message);
    } finally {
      setLoadingDrift(false);
    }
  };

  const toggleDriftItem = (key) => {
    setSelectedDriftItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAllDrift = () => {
    if (!driftReport) return;
    setSelectedDriftItems(new Set(driftReport.items.map(i => `${i.appServiceName}|${i.settingName}`)));
  };

  const handleFixDrift = async () => {
    if (!driftReport || selectedDriftItems.size === 0) return;
    setFixLoading(true);
    setFixResults(null);
    try {
      const targets = [];
      for (const key of selectedDriftItems) {
        const [appServiceName, settingName] = key.split('|');
        const item = driftReport.items.find(i => i.appServiceName === appServiceName && i.settingName === settingName);
        if (!item) continue;
        // Derive resource group from app list
        const app = apps.find(a => a.name === appServiceName);
        targets.push({ appServiceName, resourceGroup: app?.resourceGroup || '', settingName });
      }
      const results = await fixDrift({ apiType: driftApiType, targets, tenantValues: {} });
      setFixResults(results);
      // Re-run drift to refresh
      const report = await detectDrift(driftApiType, []);
      setDriftReport(report);
      setSelectedDriftItems(new Set());
    } catch (err) {
      setDriftError(err.message);
    } finally {
      setFixLoading(false);
    }
  };

  // ── Grouped drift items (by tenant) ──────────────────────────
  const groupedDrift = useMemo(() => {
    if (!driftReport) return {};
    const groups = {};
    for (const item of driftReport.items) {
      const key = item.tenantName || item.appServiceName;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    }
    return groups;
  }, [driftReport]);

  // ════════════════════════════════════════════════════════════════
  // TABS
  // ════════════════════════════════════════════════════════════════
  const tabs = [
    { id: 'templates', label: 'Templates' },
    { id: 'drift', label: 'Drift Detection' },
  ];

  // ════════════════════════════════════════════════════════════════
  // RENDER: TEMPLATES TAB
  // ════════════════════════════════════════════════════════════════
  const renderTemplatesTab = () => (
    <div className="space-y-6">
      {/* Seed Buttons */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={handleSeed}
          disabled={seeding}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
        >
          {seeding && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          Seed New Templates
        </button>
        <button
          onClick={handleMergeSeed}
          disabled={seeding}
          className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50 transition-colors flex items-center gap-2"
        >
          {seeding && <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
          Update Existing (Merge)
        </button>
        {seedResult && (
          <span className="text-sm text-gray-600 dark:text-slate-400">
            {seedResult.error ? seedResult.error : (seedResult.message || `Seeded ${seedResult.seeded?.length || 0} templates`)}
          </span>
        )}
        <span className="text-sm text-gray-500 dark:text-slate-400">
          {templates.length} template{templates.length !== 1 ? 's' : ''} loaded
        </span>
      </div>

      {loadingTemplates ? (
        <div className="flex items-center gap-3 py-8 justify-center">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500 dark:text-slate-400">Loading templates...</span>
        </div>
      ) : (
        <div className="grid gap-4">
          {templates.map((t) => (
            <div
              key={t.apiType}
              className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t.displayName}</h3>
                  <span className="text-xs font-mono text-gray-500 dark:text-slate-400">{t.apiType}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 dark:text-slate-400">
                    {t.settings.length} settings
                    {' \u00B7 '}
                    {t.settings.filter(s => s.settingType === 1).length} conn strings
                  </span>
                  <button
                    onClick={() => startEdit(t)}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                </div>
              </div>

              {/* Setting tier breakdown */}
              <div className="flex gap-2 flex-wrap">
                {[0, 1, 2].map(tier => {
                  const count = t.settings.filter(s => s.tier === tier).length;
                  if (count === 0) return null;
                  return <Badge key={tier} className={TIER_COLORS[tier]}>{count} {TIER_LABELS[tier]}</Badge>;
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 w-full max-w-5xl mx-4 mb-10">
            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Template</h2>
                <span className="text-sm font-mono text-gray-500 dark:text-slate-400">{editingTemplate.apiType}</span>
              </div>
              <button onClick={() => setEditingTemplate(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-xl">&times;</button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Name</label>
                <input
                  type="text"
                  value={editingTemplate.displayName}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, displayName: e.target.value })}
                  className="w-full max-w-md px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Settings Table */}
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-slate-900/50">
                      <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-slate-300">Name</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-slate-300">Value</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-slate-300 w-28">Tier</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-slate-300 w-32">Type</th>
                      <th className="px-3 py-2 w-16 font-semibold text-gray-700 dark:text-slate-300">Slot</th>
                      <th className="px-3 py-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                    {editingTemplate.settings.map((s, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                        <td className="px-3 py-2">
                          <input
                            type="text" value={s.name}
                            onChange={(e) => updateSetting(i, 'name', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-xs font-mono bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text" value={s.value}
                            onChange={(e) => updateSetting(i, 'value', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-xs font-mono bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={s.tier}
                            onChange={(e) => updateSetting(i, 'tier', Number(e.target.value))}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                          >
                            <option value={0}>Global</option>
                            <option value={1}>Per-Service</option>
                            <option value={2}>Per-Tenant</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={s.settingType}
                            onChange={(e) => updateSetting(i, 'settingType', Number(e.target.value))}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                          >
                            <option value={0}>App Setting</option>
                            <option value={1}>Conn String</option>
                          </select>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox" checked={s.slotSetting}
                            onChange={(e) => updateSetting(i, 'slotSetting', e.target.checked)}
                            className="rounded w-4 h-4"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => removeSetting(i)} className="text-red-500 hover:text-red-700 text-xs font-bold">&times;</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button onClick={addSetting} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                + Add setting
              </button>

              {saveError && (
                <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{saveError}</div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-slate-700 flex justify-end gap-3">
              <button onClick={() => setEditingTemplate(null)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800">
                Cancel
              </button>
              <button
                onClick={saveTemplate}
                disabled={saveLoading}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {saveLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDER: DRIFT DETECTION TAB
  // ════════════════════════════════════════════════════════════════
  const renderDriftTab = () => (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Detect Configuration Drift</h3>
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-xs">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">API Template</label>
            <select
              value={driftApiType}
              onChange={(e) => { setDriftApiType(e.target.value); setDriftReport(null); setFixResults(null); }}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a template...</option>
              {templates.map(t => <option key={t.apiType} value={t.apiType}>{t.displayName}</option>)}
            </select>
          </div>
          <button
            onClick={handleDetectDrift}
            disabled={!driftApiType || loadingDrift}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loadingDrift && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            Scan for Drift
          </button>
        </div>
        {driftError && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{driftError}</div>
        )}
      </div>

      {/* Results */}
      {driftReport && (
        <>
          {/* Summary */}
          <div className="flex flex-wrap gap-3">
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
              {driftReport.matchCount} OK
            </Badge>
            <Badge className={DRIFT_COLORS[1]}>
              {driftReport.missingCount} Missing
            </Badge>
            <Badge className={DRIFT_COLORS[2]}>
              {driftReport.staleCount} Stale
            </Badge>
            <Badge className="bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400">
              {driftReport.totalApps} Apps scanned
            </Badge>
          </div>

          {driftReport.items.length === 0 ? (
            <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl p-8 text-center">
              <p className="text-green-700 dark:text-green-400 font-medium">All settings match the template. No drift detected.</p>
            </div>
          ) : (
            <>
              {/* Fix controls */}
              <div className="flex items-center gap-3">
                <button onClick={selectAllDrift} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  Select all ({driftReport.items.length})
                </button>
                {selectedDriftItems.size > 0 && (
                  <button
                    onClick={handleFixDrift}
                    disabled={fixLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    {fixLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    Fix {selectedDriftItems.size} selected
                  </button>
                )}
                {selectedDriftItems.size > 0 && (
                  <button onClick={() => setSelectedDriftItems(new Set())} className="text-sm text-gray-500 dark:text-slate-400 hover:underline">
                    Clear selection
                  </button>
                )}
              </div>

              {/* Fix results */}
              {fixResults && (
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Fix Results</h4>
                  <div className="flex gap-2 flex-wrap">
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      {fixResults.filter(r => r.success).length} Succeeded
                    </Badge>
                    {fixResults.filter(r => !r.success).length > 0 && (
                      <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                        {fixResults.filter(r => !r.success).length} Failed
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Drift table grouped by tenant */}
              {Object.entries(groupedDrift).map(([tenant, items]) => (
                <div key={tenant} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 dark:bg-slate-900/50 border-b border-gray-200 dark:border-slate-700">
                    <span className="font-semibold text-gray-900 dark:text-white">{tenant}</span>
                    <span className="ml-2 text-xs text-gray-500 dark:text-slate-400">
                      {items.length} issue{items.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50/50 dark:bg-slate-900/30">
                        <th className="w-10 px-3 py-2"></th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-slate-300">Setting</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-slate-300 w-24">Status</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-slate-300 w-24">Tier</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-slate-300 w-24">Type</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-slate-300">Expected</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-slate-300">Actual</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                      {items.map((item) => {
                        const key = `${item.appServiceName}|${item.settingName}`;
                        const hasUnresolvedPlaceholder = item.expectedValue && /\{[^}]+\}/.test(item.expectedValue);
                        const isFixable = !hasUnresolvedPlaceholder;
                        return (
                          <tr key={key} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                            <td className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={selectedDriftItems.has(key)}
                                onChange={() => toggleDriftItem(key)}
                                className="rounded w-4 h-4"
                                disabled={!isFixable && item.status === 1} // can't fix missing per-tenant without values
                              />
                            </td>
                            <td className="px-3 py-2 font-mono text-xs text-gray-900 dark:text-white">{item.settingName}</td>
                            <td className="px-3 py-2">
                              <Badge className={DRIFT_COLORS[item.status]}>
                                {item.status === 1 ? 'Missing' : 'Stale'}
                              </Badge>
                            </td>
                            <td className="px-3 py-2">
                              <Badge className={TIER_COLORS[item.tier]}>{TIER_LABELS[item.tier]}</Badge>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-slate-400">
                              {SETTING_TYPE_LABELS[item.settingType]}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs text-gray-500 dark:text-slate-400 max-w-xs truncate">
                              {item.isMasked ? '********' : (item.expectedValue || '\u2014')}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs text-gray-500 dark:text-slate-400 max-w-xs truncate">
                              {item.actualValue || '\u2014'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );

  // ════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <div className="max-w-7xl mx-auto">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200 dark:border-slate-700 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 hover:border-gray-300 dark:hover:border-slate-500'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'templates' && renderTemplatesTab()}
      {activeTab === 'drift' && renderDriftTab()}
    </div>
  );
};

export default EnvManager;
